import { DownOutlined } from "@ant-design/icons";
import {
	Button,
	type DrawerProps,
	Dropdown,
	Form,
	type FormInstance,
	Grid,
	Space,
	Steps,
} from "antd";
import { useEffect, useState } from "react";
import { CustomDrawer } from "./layout/workspace/custom-drawer";

interface WizardPage {
	id: string;
	title: string;
	content: React.ReactNode;
	/** Field names validated before advancing past this page; a failure blocks "Next". */
	fields?: string[];
}

interface WizardProps extends DrawerProps {
	open: boolean;
	title: string;
	pages: WizardPage[];
	form?: FormInstance;
	completeLabel?: string;
	onComplete: () => void;
	/** Optional alternate actions attached to the complete button on the last page as a split-button dropdown. */
	completeMenu?: {
		key: string;
		label: string;
		onClick: () => void;
		disabled?: boolean;
	}[];
	onCancel: () => void;
	onStepChange?: (step: number) => void;
	/** Full-page overlay message shown while `loading` (e.g. "Creating agent…"). */
	loadingMessage?: React.ReactNode;
}

/** A multi-step wizard rendered in a right-side Drawer. Full screen on mobile. */
export function Wizard({
	open,
	title,
	pages,
	completeLabel = "Complete",
	onComplete,
	completeMenu,
	onCancel,
	onStepChange,
	form,
	// Kept out of the Drawer props: the Drawer's own `loading` renders a body
	// skeleton; we only want it to disable the nav buttons.
	loading,
	loadingMessage,
	...props
}: WizardProps) {
	const [step, setStep] = useState(0);
	const isLast = step === pages.length - 1;
	const screens = Grid.useBreakpoint();
	const isMobile = !screens.md;
	const [submittable, setSubmittable] = useState(false);
	// Whether the current page's declared fields pass validation — drives the "Next" disabled state.
	const [pageValid, setPageValid] = useState(true);

	const goTo = (next: number) => {
		setStep(next);
		onStepChange?.(next);
	};

	// Reset step when the drawer closes so a re-open always starts at page 0.
	useEffect(() => {
		if (!open) {
			setStep(0);
			onStepChange?.(0);
		}
	}, [open, onStepChange]);

	const handleClose = () => {
		onCancel();
	};
	const handleNext = () => {
		if (isLast) {
			onComplete();
			return;
		}
		// Validate the current page's fields before advancing; antd surfaces the errors inline.
		const fields = pages[step]?.fields;
		if (fields?.length && form) {
			form
				.validateFields(fields)
				.then(() => goTo(step + 1))
				.catch(() => {});
		} else {
			goTo(step + 1);
		}
	};

	const values = Form.useWatch([], form);
	const pageFields = pages[step]?.fields;
	// biome-ignore lint/correctness/useExhaustiveDependencies: pageFields is a fresh array each render; key on its contents + step.
	useEffect(() => {
		if (!form || !values) return;
		let cancelled = false;
		// The field's own onChange validation can supersede this one mid-flight;
		// rc-field-form then rejects with outOfDate and no errors. Retry until a
		// validation completes cleanly, otherwise the button sticks disabled.
		const validateAll = () => {
			form
				.validateFields({ validateOnly: true })
				.then(() => {
					if (!cancelled) setSubmittable(true);
				})
				.catch((e: { outOfDate?: boolean }) => {
					if (cancelled) return;
					if (e?.outOfDate) return validateAll();
					setSubmittable(false);
				});
		};
		validateAll();
		// Validate only the current page's fields to gate "Next"; pages without fields are never blocked.
		if (!pageFields?.length) {
			setPageValid(true);
		} else {
			form
				.validateFields(pageFields, { validateOnly: true })
				.then(() => setPageValid(true))
				.catch(() => setPageValid(false));
		}
		return () => {
			cancelled = true;
		};
	}, [values, form, step, pageFields?.join(",")]);

	return (
		<CustomDrawer
			{...props}
			title={isMobile ? "" : title}
			open={open}
			onClose={handleClose}
			loadingMessage={loading ? loadingMessage : undefined}
			footer={
				<Space style={{ width: "100%", justifyContent: "flex-end" }}>
					{step > 0 && (
						<Button disabled={loading} onClick={() => goTo(step - 1)}>
							Back
						</Button>
					)}
					{isLast && completeMenu?.length ? (
						<Space.Compact>
							<Button
								type="primary"
								disabled={loading || !submittable}
								onClick={handleNext}
							>
								{completeLabel}
							</Button>
							<Dropdown
								disabled={loading || !submittable}
								menu={{ items: completeMenu }}
							>
								<Button
									type="primary"
									disabled={loading || !submittable}
									icon={<DownOutlined />}
								/>
							</Dropdown>
						</Space.Compact>
					) : (
						<Button
							type="primary"
							disabled={loading || (isLast ? !submittable : !pageValid)}
							onClick={handleNext}
						>
							{isLast ? completeLabel : "Next"}
						</Button>
					)}
				</Space>
			}
		>
			{!isMobile && pages.length > 1 && (
				<Steps
					current={step}
					orientation="horizontal"
					size="small"
					style={{ marginBottom: 24 }}
					items={pages.map((p) => ({ title: p.title }))}
				/>
			)}

			{form && (
				<Form form={form} layout="vertical">
					{pages.map((page, i) => (
						<div
							key={page.id}
							style={{ display: i === step ? undefined : "none" }}
						>
							{page.content}
						</div>
					))}
				</Form>
			)}
			{!form &&
				pages.map((page, i) => (
					<div
						key={page.id}
						style={{ display: i === step ? undefined : "none" }}
					>
						{page.content}
					</div>
				))}
		</CustomDrawer>
	);
}
